import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FaqCategoryListParams, FaqCategoryPayload, FaqCategoryReorderPayload } from 'Modules'

import { AppAdmin } from 'api/app-admin'
import { revalidateWebUser } from 'utils/revalidate-web-user'

import { FaqCategoryListCache } from './faq-list-optimistic'
import { faqCategoryKeys } from './query-keys'

const invalidateList = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: faqCategoryKeys.lists() })
}

export const useCreateFaqCategory = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: FaqCategoryPayload) => {
      const res = await AppAdmin.createFaqCategory(payload)
      return res.data?.data
    },
    onSuccess: () => {
      invalidateList(queryClient)
      revalidateWebUser(['faq'])
    },
  })
}

export const useUpdateFaqCategory = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: FaqCategoryPayload }) => {
      const res = await AppAdmin.updateFaqCategory(id, payload)
      return res.data?.data
    },
    onSuccess: () => {
      invalidateList(queryClient)
      revalidateWebUser(['faq'])
    },
  })
}

type ReorderCategoryVariables = {
  id: string
  payload: FaqCategoryReorderPayload
  nextResults?: FaqCategoryListCache['results']
}

export const useReorderFaqCategory = (listParams: FaqCategoryListParams = {}) => {
  const queryClient = useQueryClient()
  const queryKey = faqCategoryKeys.list(listParams)

  return useMutation({
    mutationFn: async ({ id, payload }: ReorderCategoryVariables) => {
      const res = await AppAdmin.reorderFaqCategory(id, payload)
      return res.data?.data
    },
    onMutate: async ({ nextResults }) => {
      if (!nextResults) {
        return {}
      }
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<FaqCategoryListCache>(queryKey)
      queryClient.setQueryData<FaqCategoryListCache>(queryKey, (old) =>
        old ? { ...old, results: nextResults } : old,
      )
      return { previous }
    },
    onSuccess: () => revalidateWebUser(['faq']),
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
      void queryClient.invalidateQueries({ queryKey })
    },
  })
}

export const useDeleteFaqCategory = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await AppAdmin.deleteFaqCategory(id)
    },
    onSuccess: () => {
      invalidateList(queryClient)
      revalidateWebUser(['faq'])
    },
  })
}

export const useCheckFaqCategoryName = () => {
  return useMutation({
    mutationFn: async ({ name, excludeId }: { name: string; excludeId?: string }) => {
      const res = await AppAdmin.checkFaqCategoryName(name.trim(), excludeId)
      return res.data?.data?.available ?? false
    },
  })
}
